import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TokenClaims } from '@sal/interfaces';
import { TenantRoles } from '@sal/shared/domain/value-objects';
import { TenantAuthorizationService } from '../../auth/services/tenant-authorization.service';

@Injectable({
    providedIn: 'root'
})
export class TenantGuard {
    private router = inject(Router);
    private msalService = inject(MsalService);
    private tenantAuthorizationService = inject(TenantAuthorizationService);

    // Ensures the energy tenant lands on the startpage only for the very first navigation after an app
    // load/refresh. Once that initial navigation has resolved, in-app navigation to other routes (e.g. /news)
    // must work normally instead of always being bounced back to /map.
    private hasCompletedInitialNavigation = false;

    canActivate(_next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        const tokenClaims = this.getTokenClaims();
        const activeTenantRole = this.tenantAuthorizationService.resolveActiveTenantRole(tokenClaims);
        if (activeTenantRole) {
            this.tenantAuthorizationService.storeActiveTenantRole(activeTenantRole);
            if (
                activeTenantRole === TenantRoles.SE &&
                !this.hasCompletedInitialNavigation &&
                !this.isStartpageRoute(state?.url)
            ) {
                return this.router.createUrlTree(['/map']);
            }

            this.hasCompletedInitialNavigation = true;
            return true;
        }

        this.tenantAuthorizationService.clearActiveTenantRole();

        // Not authorized for the current tenant domain: fall back to the first tenant the user has an Entra role for.
        const fallbackTenantRole = this.tenantAuthorizationService.resolveFirstAccessibleTenantRole(tokenClaims);
        if (!fallbackTenantRole) {
            return this.router.createUrlTree(['/unauthorized']);
        }

        const targetPath = fallbackTenantRole === TenantRoles.SE ? '' : state.url;
        this.navigateToExternalUrl(
            this.tenantAuthorizationService.buildTenantRedirectUrl(fallbackTenantRole, targetPath)
        );
        return false;
    }

    private isStartpageRoute(url?: string): boolean {
        if (!url) {
            return true;
        }
        const [pathname] = url.split(/[?#]/);
        return pathname === '' || pathname === '/' || pathname === '/map';
    }

    private getTokenClaims(): TokenClaims | undefined {
        const activeAccount =
            this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()[0] ?? null;
        return activeAccount?.idTokenClaims as TokenClaims | undefined;
    }

    private navigateToExternalUrl(url: string): void {
        window.location.href = url;
    }
}
